import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format, parse } from 'date-fns';
import { enGB } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import { Comment } from '../../types';

const DEBUG: number = 1; // Debug flag: Set to 1 to enable console logging, 0 to disable

// Debug logging helper
const debugLog = (...args: any[]) => {
  if (DEBUG > 0) {
    console.log(...args);
  }
};

interface CommentSectionProps {
  dealId: number;
}

const CommentSection: React.FC<CommentSectionProps> = ({ dealId }) => {
  const { isAuthenticated, user } = useAuth();
  const [newComment, setNewComment] = useState('');
  const queryClient = useQueryClient();

  // Fetch comments for the deal
  const {
    data: comments,
    isLoading,
    isError,
  } = useQuery<Comment[]>(
    ['comments', dealId],
    async () => {
      const response = await fetch(`/api/comments/${dealId}`);
      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to fetch comments');
      }

      if (response.status === 404) {
        return [];
      }

      return response.json();
    },
    {
      enabled: !!dealId,
    }
  );

  // Add comment mutation
  const addCommentMutation = useMutation(
    async () => {
      if (!newComment.trim()) {
        throw new Error('Comment cannot be empty');
      }

      debugLog('Posting comment for dealId:', dealId);

      const response = await fetch(`/api/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ 
          dealId: parseInt(dealId.toString(), 10),
          text: newComment.trim() 
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        debugLog('Comment post error:', error);
        throw new Error(error.error || 'Failed to add comment');
      }

      return response.json();
    },
    {
      onSuccess: (data) => {
        debugLog('Comment posted successfully:', data);
        setNewComment('');
        queryClient.invalidateQueries(['comments', dealId]);
        toast.success('Comment added successfully');
      },
      onError: (error: any) => {
        debugLog('Comment mutation error:', error);
        toast.error(error.message || 'Failed to add comment');
      },
    }
  );

  // Delete comment mutation
  const deleteCommentMutation = useMutation(
    async (commentId: number) => {
      const response = await fetch(`/api/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete comment');
      }

      return response.json();
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['comments', dealId]);
        toast.success('Comment deleted successfully');
      },
      onError: (error: any) => {
        toast.error(error.message || 'Failed to delete comment');
      },
    }
  );

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Please log in to add a comment');
      return;
    }

    debugLog('Submitting comment for deal:', dealId);
    addCommentMutation.mutate();
  };

  // Handle comment deletion
  const handleDeleteComment = (commentId: number) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      deleteCommentMutation.mutate(commentId);
    }
  };

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <h2 className="text-xl font-bold text-gray-900">Comments</h2>

      {/* Comment form */}
      {isAuthenticated ? (
        <form onSubmit={handleSubmit} className="mt-4">
          <div>
            <label htmlFor="comment" className="sr-only">
              Add a comment
            </label>
            <textarea
              id="comment"
              rows={3}
              className="block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder="Join the discussion..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
            ></textarea>
          </div>

          <div className="mt-2 flex justify-end">
            <button
              type="submit"
              disabled={addCommentMutation.isLoading || !newComment.trim()}
              className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {addCommentMutation.isLoading ? 'Posting...' : 'Post Comment'}
            </button>
          </div>
        </form>
      ) : (
        <div className="mt-4 rounded-md bg-gray-50 p-4 text-center">
          <p className="text-sm text-gray-700">
            Please{' '}
            <a href="/login" className="font-medium text-primary-600 hover:text-primary-500">
              sign in
            </a>{' '}
            to join the discussion
          </p>
        </div>
      )}

      {/* Comments list */}
      <div className="mt-6 divide-y divide-gray-200">
        {isLoading ? (
          // Loading state
          <div className="space-y-4 py-4">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 rounded-full bg-gray-200"></div>
                  <div className="h-4 w-24 rounded bg-gray-200"></div>
                </div>
                <div className="mt-2 h-4 w-3/4 rounded bg-gray-200"></div>
                <div className="mt-1 h-4 w-1/2 rounded bg-gray-200"></div>
              </div>
            ))}
          </div>
        ) : isError ? (
          // Error state
          <div className="py-4 text-center">
            <p className="text-sm text-red-600">Failed to load comments. Please try again.</p>
          </div>
        ) : comments?.length === 0 ? (
          // Empty state
          <div className="py-4 text-center">
            <p className="text-sm text-gray-500">No comments yet. Be the first to comment!</p>
          </div>
        ) : (
          // Comments list
          (() => {
            debugLog('Comments data:', comments);
            return comments?.map((comment) => (
              <div key={comment.id} className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center">
                    <div className="h-8 w-8 overflow-hidden rounded-full bg-gray-200">
                      {comment.avatarUrl ? (
                        <img
                          src={comment.avatarUrl}
                          alt={`${comment.username}'s avatar`}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-gray-100 text-gray-400">
                          {comment.username.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>

                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">
                        {comment.username || 'Unknown User'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(() => {
                          debugLog('comment.created_at:', comment.created_at);
                          return format(new Date(comment.created_at), 'PP p');
                        })()}
                      </p>
                    </div>
                  </div>

                  {/* Delete button (only for own comments or moderators/admins) */}
                  {(user?.id === comment.userId || user?.role === 'admin' || user?.role === 'moderator') && (
                    <button
                      onClick={() => handleDeleteComment(comment.id)}
                      className="text-sm text-gray-400 hover:text-red-600"
                      aria-label="Delete comment"
                    >
                      Delete
                    </button>
                  )}
                </div>

                <div className="mt-2 text-sm text-gray-700">
                  <p>{comment.text}</p>
                </div>
              </div>
            ));
          })()
        )}
      </div>
    </div>
  );
};

export default CommentSection;
